"use client";

import { parseGeoPrefCookieValue } from "@/lib/geo/geo-pref";
import { readGeoPrefCookieRaw } from "@/lib/geo/geo-pref-client";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";

export type PricingResolveItem = { id: string } & ResolvedMoneyPresentation;

/** Shared normalized cookie input for the request and its client response key. */
export function readPricingReferenceCurrency(): string | null {
	return parseGeoPrefCookieValue(readGeoPrefCookieRaw())?.referenceCurrency ?? null;
}

/**
 * Resolve display labels for arbitrary money amounts (reference currency + FX).
 * Uses the items-only API contract; every item keeps its own settlement currency.
 */
export async function fetchPricingResolveItems(params: {
	locale: string;
	channel: string;
	items: Array<{ id: string; amount: number; currency: string }>;
	/** A supplied snapshot must stay identical to the context used by the caller's response key. */
	referenceCurrency?: string | null;
	signal?: AbortSignal;
}): Promise<PricingResolveItem[] | null> {
	if (params.signal?.aborted) return null;
	if (params.items.length === 0) return [];

	const referenceCurrency =
		params.referenceCurrency === undefined ? readPricingReferenceCurrency() : params.referenceCurrency;

	try {
		const res = await fetch("/api/pricing/resolve", {
			method: "POST",
			signal: params.signal,
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				locale: params.locale,
				channel: params.channel,
				country: null,
				referenceCurrency,
				items: params.items,
			}),
		});
		if (!res.ok) return null;
		const data = (await res.json()) as { items: PricingResolveItem[] };
		return data.items;
	} catch {
		return null;
	}
}

export function pricingItemsById(items: PricingResolveItem[]): Map<string, ResolvedMoneyPresentation> {
	const m = new Map<string, ResolvedMoneyPresentation>();
	for (const it of items) {
		const { id, ...pres } = it;
		m.set(id, pres);
	}
	return m;
}
